import React, { useEffect, useState } from 'react';
import BEM from '@upsales/components/Utils/bemClass';
import { Card, Flex, Title, Text, Link, Icon, type IconName } from '@upsales/components';
import T from 'Components/Helpers/translate';

import './ContactOutreachPlan.scss';

type ContactEngagementStep = {
	planId: number;
	day: number;
	channel: string;
	type: string;
	subject: string | null;
	status: string;
};

type Props = {
	contactId: number;
};

// Steps that are still out (sent/completed) vs outstanding — a plan whose every
// step for this contact is done has nothing left to act on and is hidden.
const DONE_STATUSES = new Set(['sent', 'completed']);

const CHANNEL_ICON: Record<string, IconName> = {
	email: 'envelope',
	call: 'phone',
	linkedin: 'linkedin',
	todo: 'check-square-o'
};

// Reuse the review page's channel/status keys, falling back to the raw value.
const labelFor = (prefix: string, value: string): string => {
	const key = `prospectingAgent.${prefix}.${value}`;
	const translated = T(key);
	return translated && translated !== key ? translated : value;
};

/**
 * The detailed outreach view for the contact card (WEB-8617): this contact's own
 * step sequence (channel · day · status) for each active engagement plan, deep-linking
 * into the plan review. The account card carries the plan-level reference; here a rep
 * sees exactly what the agent is doing to reach *this* person. Self-fetches keyed by
 * contactId, gated server-side behind OUTREACH_AGENT, and renders null when empty.
 */
const ContactOutreachPlan = ({ contactId }: Props) => {
	const [steps, setSteps] = useState<ContactEngagementStep[] | null>(null);

	useEffect(() => {
		if (!contactId || contactId <= 0) {
			setSteps(null);
			return;
		}
		let cancelled = false;
		fetch(`/api/v2/prospectingAgentRuns/contact/${contactId}/engagementSteps`)
			.then(r => r.json())
			.then(body => {
				if (cancelled) return;
				const data: ContactEngagementStep[] = Array.isArray(body?.data)
					? body.data
					: Array.isArray(body)
					? body
					: [];
				setSteps(data);
			})
			.catch(err => {
				console.error('[ContactOutreachPlan] failed to load engagement steps', err);
			});
		return () => {
			cancelled = true;
		};
	}, [contactId]);

	// Group the flat step list by plan (newest first, preserving the day order the
	// API returns), then drop any plan whose steps are all done.
	const groups: { planId: number; steps: ContactEngagementStep[] }[] = [];
	for (const step of steps ?? []) {
		const last = groups[groups.length - 1];
		if (last && last.planId === step.planId) {
			last.steps.push(step);
		} else {
			groups.push({ planId: step.planId, steps: [step] });
		}
	}
	const visible = groups.filter(group => group.steps.some(step => !DONE_STATUSES.has(step.status)));

	if (!visible.length) {
		return null;
	}

	const CSS = new BEM('ContactOutreachPlan');

	return (
		<div className={CSS.b()}>
			{visible.map(group => (
				<Link
					key={group.planId}
					href={`#/prospecting-agent?plan=${group.planId}&from=plans`}
					nostyle={true}
					aria-label={T('prospectingAgent.engagementPlan')}
					className={CSS.elem('plan').b()}
				>
					<Card className={CSS.elem('card').b()}>
						<Flex justifyContent="space-between" alignItems="center" gap={8}>
							<Flex alignItems="center" gap={8}>
								<Icon name="paper-plane" color="bright-blue" />
								<Title size="sm">{T('prospectingAgent.engagementPlan')}</Title>
							</Flex>
							<Icon name="chevron-right" color="grey-8" />
						</Flex>

						<div className={CSS.elem('steps').b()}>
							{group.steps.map((step, i) => {
								const done = DONE_STATUSES.has(step.status);
								return (
									<Flex
										key={i}
										alignItems="center"
										gap={10}
										className={CSS.elem('step').mod({ done }).b()}
									>
										<Icon name={CHANNEL_ICON[step.channel] || 'circle'} color="grey-10" />
										<div className={CSS.elem('step-main').b()}>
											<Text size="sm" ellipsis={true}>
												{step.subject || labelFor('channel', step.channel)}
											</Text>
											<Text size="sm" color="grey-10">
												{(T('prospectingAgent.stepDay') || 'Day {n}').replace(
													'{n}',
													String(step.day)
												)}
												{' · '}
												{labelFor('engagementStatus', step.status)}
											</Text>
										</div>
									</Flex>
								);
							})}
						</div>
					</Card>
				</Link>
			))}
		</div>
	);
};

export default ContactOutreachPlan;
